import React, { useState } from 'react';
import { pdf } from '@react-pdf/renderer';
import HealthReportPDF from './HealthReportPDF';
import { convertFxRange } from '../../util/GenericUtil';
import '../../styles/buttons.css';
import './ExportButton.css';

/**
 * Export button that generates and downloads a PDF health report.
 * Collects all necessary data from props and passes to PDF generator.
 */
function ExportButton({
    testDate,
    patientInfo,
    systemScores,
    tests,
    recommendations = [],
    recommendationMode,
    getFxRange,
    focuses,
    assessmentSummary,
    sex,
    variant = 'default',
    disabled = false
}) {
    const [isGenerating, setIsGenerating] = useState(false);

    const handleExport = async () => {
        if (isGenerating) return;

        setIsGenerating(true);
        try {
            // Prepare test data with fx ranges (converted to lab units)
            const testsWithRanges = tests.map(test => {
                const rawFxRange = getFxRange(test.alias || test.test_name, 'male');
                const fxRange = convertFxRange(rawFxRange, test.alias || test.test_name, test.units);
                return {
                    ...test,
                    fx_low: fxRange?.fx_low ?? null,
                    fx_high: fxRange?.fx_high ?? null
                };
            });

            // Generate PDF blob
            const blob = await pdf(
                <HealthReportPDF
                    testDate={testDate}
                    patientInfo={patientInfo}
                    systemScores={systemScores}
                    tests={testsWithRanges}
                    recommendations={recommendations}
                    recommendationMode={recommendationMode}
                    focuses={focuses}
                    assessmentSummary={assessmentSummary}
                    sex={sex}
                />
            ).toBlob();

            // Create download link
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            // Generate filename with date
            const dateStr = testDate
                ? testDate.toString().replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3')
                : new Date().toISOString().split('T')[0];
            link.download = `health-report-${dateStr}.pdf`;

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error generating PDF:', error);
            alert('Failed to generate PDF. Please try again.');
        } finally {
            setIsGenerating(false);
        }
    };

    const isIcon = variant === 'icon';
    const isHeader = variant === 'header';
    const isDropdown = variant === 'dropdown';

    return (
        <button
            className={`exportButton ${isGenerating ? 'generating' : ''} ${isIcon ? 'btn-icon icon-variant' : ''} ${isHeader ? 'btn-pill header-variant' : ''} ${isDropdown ? 'dropdown-variant' : ''}`}
            onClick={handleExport}
            disabled={disabled || isGenerating || !tests?.length}
            title={isGenerating ? 'Generating PDF...' : 'Download Report'}
        >
            {isIcon ? (
                isGenerating ? (
                    <span style={{ fontSize: '14px' }}>...</span>
                ) : (
                    <span>&#8681;</span>
                )
            ) : (
                isGenerating ? 'Generating...' : 'Download Report'
            )}
        </button>
    );
}

export default ExportButton;
